// Group encryption helpers — enabling a group's key and keying its members.
// All crypto is client-side; the server only stores opaque wrapped blobs.
import api from '../services/api';
import {
  generateKeyPair, exportPublicKey, toBase64url,
  wrapPrivateKeyForApp, unwrapPrivateKeyFromApp,
} from './fieldEncryption';
import { forgetGroupKeys } from './spaceKeys';

// INITIAL enable — generate a FRESH group keypair and wrap it for every member who
// has a personal key. No password unlock needed (only public keys + a new keypair).
// Use ONLY on a group that is not yet encrypted; regenerating the key on an already-
// encrypted group would orphan existing content. Returns { keyed, pending }.
export async function enableGroupEncryptionInitial(groupId) {
  const { members } = await api.request(`/groups/${groupId}/member-enc-keys`);
  const withKeys = (members || []).filter((m) => m.enc_public_key);
  if (!withKeys.length) return { keyed: 0, pending: (members || []).length };
  const kp = await generateKeyPair();
  const group_enc_public_key = toBase64url(await exportPublicKey(kp.publicKey));
  const member_keys = [];
  for (const m of withKeys) {
    member_keys.push({
      user_id: m.user_id,
      enc_group_privkey: await wrapPrivateKeyForApp(kp.privateKey, m.enc_public_key),
    });
  }
  await api.request(`/groups/${groupId}/enc-keys`, {
    method: 'POST', body: { group_enc_public_key, member_keys },
  });
  forgetGroupKeys(groupId);
  return { keyed: withKeys.length, pending: (members || []).length - withKeys.length };
}

// BACKFILL — key any members who have a personal key but no group key yet, REUSING
// the group's EXISTING key (never regenerates it). Needs the caller's unlocked user
// private key to unwrap the group key before re-wrapping for the pending members.
// Returns { keyed, pending } (pending = members still without a personal key).
export async function backfillGroupMembers(groupId, userPrivateKey) {
  const { members } = await api.request(`/groups/${groupId}/member-enc-keys`);
  const pending = (members || []).filter((m) => m.enc_public_key && !m.has_group_key);
  const noKeyYet = (members || []).filter((m) => !m.enc_public_key).length;
  if (!pending.length) return { keyed: 0, pending: noKeyYet };
  const gk = await api.request(`/groups/${groupId}/enc-privkey`);
  if (!gk?.enc_group_privkey) throw new Error('You do not hold this group key.');
  const groupPriv = await unwrapPrivateKeyFromApp(gk.enc_group_privkey, userPrivateKey);
  const member_keys = [];
  for (const m of pending) {
    member_keys.push({
      user_id: m.user_id,
      enc_group_privkey: await wrapPrivateKeyForApp(groupPriv, m.enc_public_key),
    });
  }
  await api.request(`/groups/${groupId}/enc-keys`, {
    method: 'POST', body: { group_enc_public_key: gk.enc_public_key, member_keys },
  });
  return { keyed: pending.length, pending: noKeyYet };
}
